/**
 * Credits Service
 * 
 * Handles all credit-related API calls including:
 * - Getting credit balance
 * - Getting transaction history
 * - Fetching credit packs
 * - Creating credit pack checkout sessions
 */

import { API_CONFIG, API_ENDPOINTS } from '../utils/constants';
import { cacheManager, CACHE_KEYS, CACHE_TTL } from './cacheManager';

export interface CreditBalanceResponse {
  credits: number;
  user_id: string;
}

export interface Transaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  operation_type?: string;
  notes?: string;
  created_at: string;
}

export interface TransactionHistoryResponse {
  transactions: Transaction[];
  total: number;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_usd: number;
  description: string;
}

export interface CreditPacksResponse {
  packs: CreditPack[];
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  credits: number;
  price_monthly_usd: number;
  price_annual_usd: number;
  stripe_price_id_monthly: string;
  stripe_price_id_annual: string;
}

export interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
}

export interface CreateCreditCheckoutRequest {
  pack_id: 'basic' | 'pro' | 'premium';
  email: string;
  user_id: string;
}

export interface CreateCreditCheckoutResponse {
  url: string;
  session_id: string;
}

export interface CreateCreditSetupIntentRequest {
  pack_id: 'basic' | 'pro' | 'premium';
  email: string;
  user_id: string;
}

export interface CreateCreditSetupIntentResponse {
  success: boolean;
  client_secret: string;
  customer_id: string;
  publishable_key?: string; // Optional: publishable key from backend
}

export interface CreditCheckoutSession {
  id: string;
  payment_status: string;
  credits_granted?: number;
}

export interface CreditCheckoutResponse {
  session: CreditCheckoutSession;
}

/**
 * Get current credit balance for a user
 */
export async function getCreditBalance(userId: string): Promise<CreditBalanceResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREDITS.BALANCE}?user_id=${userId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch credit balance' }));
    throw new Error(error.error || error.message || 'Failed to fetch credit balance');
  }

  return await response.json();
}

/**
 * Get transaction history for a user
 */
export async function getTransactionHistory(
  userId: string,
  limit: number = 50
): Promise<TransactionHistoryResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREDITS.HISTORY}?user_id=${userId}&limit=${limit}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch transaction history' }));
    throw new Error(error.error || error.message || 'Failed to fetch transaction history');
  }

  return await response.json();
}

/**
 * Get available credit packs (with caching)
 */
export async function getCreditPacks(): Promise<CreditPacksResponse> {
  // Check cache first
  const cached = cacheManager.get<CreditPacksResponse>(CACHE_KEYS.CREDIT_PACKS);
  if (cached) {
    // Refresh in background
    fetchCreditPacksFromAPI().then(data => {
      cacheManager.set(CACHE_KEYS.CREDIT_PACKS, data, CACHE_TTL.CREDIT_PACKS);
    }).catch(() => {
      // Silent fail for background refresh
    });
    
    return cached;
  }

  // Cache miss - fetch and store
  const data = await fetchCreditPacksFromAPI();
  cacheManager.set(CACHE_KEYS.CREDIT_PACKS, data, CACHE_TTL.CREDIT_PACKS);
  return data;
}

/**
 * Internal function to fetch credit packs from API
 */
async function fetchCreditPacksFromAPI(): Promise<CreditPacksResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREDIT_PACKS}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch credit packs' }));
    throw new Error(error.error || error.message || 'Failed to fetch credit packs');
  }

  return await response.json();
}

/**
 * Get subscription plans with Stripe prices (with caching)
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
  // Check cache first
  const cached = cacheManager.get<SubscriptionPlansResponse>(CACHE_KEYS.SUBSCRIPTION_PLANS);
  if (cached) {
    // Refresh in background
    fetchSubscriptionPlansFromAPI().then(data => {
      cacheManager.set(CACHE_KEYS.SUBSCRIPTION_PLANS, data, CACHE_TTL.SUBSCRIPTION_PLANS);
    }).catch(() => {
      // Silent fail for background refresh
    });
    
    return cached;
  }

  // Cache miss - fetch and store
  const data = await fetchSubscriptionPlansFromAPI();
  cacheManager.set(CACHE_KEYS.SUBSCRIPTION_PLANS, data, CACHE_TTL.SUBSCRIPTION_PLANS);
  return data;
}

/**
 * Internal function to fetch subscription plans from API
 */
async function fetchSubscriptionPlansFromAPI(): Promise<SubscriptionPlansResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.SUBSCRIPTION_PLANS}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch subscription plans' }));
    throw new Error(error.error || error.message || 'Failed to fetch subscription plans');
  }

  return await response.json();
}

/**
 * Create a credit pack checkout session (legacy - redirects to Stripe)
 */
export async function createCreditCheckout(
  request: CreateCreditCheckoutRequest
): Promise<CreateCreditCheckoutResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREATE_CREDIT_CHECKOUT}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
    throw new Error(error.error || error.message || 'Failed to create checkout session');
  }

  return await response.json();
}

/**
 * Create a setup intent for embedded credit pack checkout
 */
export async function createCreditSetupIntent(
  request: CreateCreditSetupIntentRequest
): Promise<CreateCreditSetupIntentResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/create-credit-setup-intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create setup intent' }));
    throw new Error(error.error || error.message || 'Failed to create setup intent');
  }

  return await response.json();
}

/**
 * Complete credit pack purchase after payment method is collected
 */
export async function completeCreditPurchase(
  customerId: string,
  paymentMethodId: string,
  packId: 'basic' | 'pro' | 'premium',
  userId: string
): Promise<{ success: boolean; credits_granted?: number }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/stripe/complete-credit-purchase`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        payment_method_id: paymentMethodId,
        pack_id: packId,
        user_id: userId,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to complete purchase' }));
    throw new Error(error.error || error.message || 'Failed to complete purchase');
  }

  return await response.json();
}

/**
 * Get credit checkout session status
 */
export async function getCreditCheckoutSession(
  sessionId: string
): Promise<CreditCheckoutResponse> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.STRIPE.CREDIT_CHECKOUT}/${sessionId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch checkout session' }));
    throw new Error(error.error || error.message || 'Failed to fetch checkout session');
  }

  return await response.json();
}

/**
 * Create or get anonymous user
 */
export async function createAnonymousUser(anonymousId: string): Promise<{ user_id: string; created: boolean }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.ANONYMOUS}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        anonymous_id: anonymousId,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create anonymous user' }));
    throw new Error(error.error || error.message || 'Failed to create anonymous user');
  }

  return await response.json();
}

/**
 * Link anonymous user to authenticated user
 */
export async function linkAnonymousUser(
  anonymousUserId: string,
  supabaseUserId: string,
  email: string
): Promise<{ success: boolean; assets_transferred: number; signup_bonus_granted: boolean }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.LINK_ANONYMOUS}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        anonymous_user_id: anonymousUserId,
        supabase_user_id: supabaseUserId,
        email: email,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to link anonymous user' }));
    throw new Error(error.error || error.message || 'Failed to link anonymous user');
  }

  return await response.json();
}

/**
 * Sign up authenticated user (grants 100 credits)
 */
export async function signupUser(
  email: string,
  supabaseUserId: string,
  fullName?: string
): Promise<{ user: any; signup_bonus_granted: boolean; credits: number }> {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.USER.SIGNUP}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        supabase_user_id: supabaseUserId,
        full_name: fullName,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to sign up user' }));
    throw new Error(error.error || error.message || 'Failed to sign up user');
  }

  return await response.json();
}

